import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SessionCard from '../components/SessionCard';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const CYAN = '#0ef8f8';
const AMBER = '#f8b60e';
const RED = '#f84e4e';

const S = {
  page:   { background: '#080808', minHeight: '100vh', padding: '20px 24px', color: '#666' },
  head:   { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 },
  h1:     { fontFamily: 'monospace', fontSize: 13, letterSpacing: '3px', color: CYAN, textTransform: 'uppercase', margin: 0 },
  sub:    { fontFamily: 'monospace', fontSize: 9, letterSpacing: '1px', color: '#333' },
  tabs:   { display: 'flex', gap: 2, marginBottom: 16, flexWrap: 'wrap' },
  tab:    (on) => ({
    fontFamily: 'monospace', fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase',
    padding: '6px 12px', cursor: 'pointer', border: '1px solid ' + (on ? 'rgba(14,248,248,0.3)' : '#1a1a1a'),
    background: on ? 'rgba(14,248,248,0.06)' : '#0d0d0d', color: on ? CYAN : '#444', borderRadius: 2,
  }),
  card:   { background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 3, padding: '14px 16px', marginBottom: 10 },
  row:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' },
  meta:   { fontFamily: 'monospace', fontSize: 8, letterSpacing: '1px', color: '#333', textTransform: 'uppercase' },
  pill:   (c) => ({
    fontFamily: 'monospace', fontSize: 8, letterSpacing: '1px', textTransform: 'uppercase',
    color: c, background: 'rgba(255,255,255,0.02)', border: `1px solid ${c}33`,
    borderRadius: 2, padding: '2px 6px',
  }),
  part:   { background: '#0a0a0a', border: '1px solid #161616', borderRadius: 2, padding: '10px 12px',
            fontSize: 12, lineHeight: 1.55, color: '#8a8a8a', whiteSpace: 'pre-wrap', marginBottom: 6 },
  ta:     { width: '100%', background: '#0a0a0a', border: '1px solid #222', borderRadius: 2, color: '#9a9a9a',
            fontSize: 12, lineHeight: 1.55, padding: '10px 12px', fontFamily: 'inherit', marginBottom: 6, resize: 'vertical' },
  btn:    (c = '#444') => ({
    fontFamily: 'monospace', fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase',
    padding: '5px 11px', cursor: 'pointer', background: 'transparent',
    border: `1px solid ${c}55`, color: c, borderRadius: 2, marginRight: 6,
  }),
  input:  { background: '#0a0a0a', border: '1px solid #222', borderRadius: 2, color: '#9a9a9a',
            fontSize: 11, padding: '6px 9px', fontFamily: 'monospace', marginRight: 6 },
  grid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 14 },
  stat:   { background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: 3, padding: '10px 12px' },
  statN:  { fontFamily: 'monospace', fontSize: 18, color: CYAN, fontWeight: 700 },
  statL:  { fontFamily: 'monospace', fontSize: 8, letterSpacing: '1px', color: '#333', textTransform: 'uppercase', marginTop: 3 },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { fontFamily: 'monospace', fontSize: 8, letterSpacing: '1px', color: '#333', textAlign: 'left',
            padding: '5px 8px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase' },
  td:     { fontSize: 10, padding: '6px 8px', borderBottom: '1px solid #111', color: '#666' },
  empty:  { fontFamily: 'monospace', fontSize: 9, color: '#2e2e2e', textAlign: 'center', padding: '30px 0' },
  note:   { fontFamily: 'monospace', fontSize: 9, color: '#555', lineHeight: 1.7 },
  banner: (c) => ({ ...S.card, borderColor: `${c}44`, background: `${c}0a`, fontFamily: 'monospace', fontSize: 9, color: c, lineHeight: 1.7 }),
};

const STATUS_COLOR = {
  draft: '#555', pending_review: AMBER, approved: CYAN,
  published: '#4ef88a', rejected: RED, archived: '#333',
};

const token = () => localStorage.getItem('xyr_dashboard_token');
const api = () => axios.create({
  baseURL: `${API_BASE}/sie`,
  headers: { Authorization: `Bearer ${token()}` },
  timeout: 180000, // generation can legitimately take a couple of minutes
});

const charCount = (s) => s.replace(/\{link\}/g, 'x'.repeat(23)).length;

/* ------------------------------------------------------------------ post */

function PostCard({ post, onChange, onToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(post.content);
  const [busy, setBusy]       = useState(false);
  const [schedule, setSchedule] = useState('');

  useEffect(() => { setDraft(post.content); }, [post.content]);

  const act = async (action, body = {}) => {
    setBusy(true);
    try {
      const { data } = await api().post(`/posts/${post.id}/${action}`, body);
      onChange(data.post || post);
      onToast(data.dry_run ? `Post ${post.id} published in DRY RUN (no X credentials)` : `Post ${post.id}: ${action} ok`);
    } catch (e) {
      const d = e.response && e.response.data;
      onToast(`Post ${post.id} ${action} failed: ${(d && d.error) || e.message}`, true);
      if (d && d.compliance) onChange({ ...post, compliance: d.compliance });
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api().put(`/posts/${post.id}`, { content: draft });
      onChange(data.post);
      setEditing(false);
      onToast(`Post ${post.id} saved`);
    } catch (e) {
      onToast(`Save failed: ${e.message}`, true);
    } finally { setBusy(false); }
  };

  const c = post.compliance;
  const violations = (c && c.violations) || [];
  const warnings   = (c && c.warnings) || [];
  const limit = post.platform === 'x' ? 280 : 3000;

  return (
    <div style={S.card}>
      <div style={S.row}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={S.pill(STATUS_COLOR[post.status] || '#444')}>{post.status.replace('_', ' ')}</span>
          <span style={S.pill('#444')}>{post.platform}</span>
          <span style={S.pill('#444')}>{post.post_type}</span>
          {post.content.length > 1 && <span style={S.pill('#666')}>thread · {post.content.length}</span>}
          {post.topic && <span style={S.meta}>{post.topic}</span>}
        </div>
        <span style={S.meta}>#{post.id}</span>
      </div>

      {post.rationale && <div style={{ ...S.note, marginBottom: 8, color: '#3d3d3d' }}>{post.rationale}</div>}

      {post.card && <SessionCard card={post.card} />}

      {violations.length > 0 && (
        <div style={S.banner(RED)}>
          <strong>BLOCKED — {violations.length} compliance violation{violations.length > 1 ? 's' : ''}</strong>
          {violations.map((v, i) => (
            <div key={i}>· [{v.rule}] {v.where}: {v.msg}{v.match ? ` — "${v.match}"` : ''}</div>
          ))}
        </div>
      )}
      {warnings.length > 0 && (
        <div style={S.banner(AMBER)}>
          {warnings.map((w, i) => <div key={i}>· [{w.rule}] {w.msg}</div>)}
        </div>
      )}

      {editing ? (
        <>
          {draft.map((part, i) => (
            <div key={i}>
              <textarea
                style={{ ...S.ta, borderColor: charCount(part) > limit ? `${RED}66` : '#222' }}
                rows={Math.max(3, Math.ceil(part.length / 70))}
                value={part}
                onChange={(e) => setDraft(draft.map((p, j) => (j === i ? e.target.value : p)))}
              />
              <div style={{ ...S.meta, textAlign: 'right', marginBottom: 8, color: charCount(part) > limit ? RED : '#333' }}>
                {charCount(part)} / {limit}
              </div>
            </div>
          ))}
          <button style={S.btn(CYAN)} onClick={save} disabled={busy}>Save</button>
          <button style={S.btn()} onClick={() => { setDraft(post.content); setEditing(false); }}>Cancel</button>
        </>
      ) : (
        <>
          {post.content.map((part, i) => (
            <div key={i} style={S.part}>
              {post.content.length > 1 && <span style={{ color: '#2e2e2e', marginRight: 8 }}>{i + 1}/{post.content.length}</span>}
              {part}
              <div style={{ ...S.meta, marginTop: 6, color: charCount(part) > limit ? RED : '#262626' }}>
                {charCount(part)} / {limit}
              </div>
            </div>
          ))}
        </>
      )}

      {/*
        Copy the caption alone. Joining the parts would put the link back in the
        post body, which is the thing splitting it into a reply avoids.
      */}
      <button
        style={S.btn('#4a6660')}
        onClick={() => {
          navigator.clipboard.writeText(post.content[0])
            .then(() => onToast('Caption copied'))
            .catch(() => onToast('Clipboard blocked by the browser', true));
        }}
      >Copy caption</button>
      {post.content.length > 1 && (
        <button
          style={S.btn('#4a6660')}
          onClick={() => {
            navigator.clipboard.writeText(post.content[1])
              .then(() => onToast('Reply copied — post this as a reply to the card'))
              .catch(() => onToast('Clipboard blocked by the browser', true));
          }}
        >Copy reply</button>
      )}

      {post.status === 'published' ? (
        <div style={{ ...S.meta, marginTop: 8 }}>
          published {post.published_at ? new Date(post.published_at).toLocaleString() : ''} ·
          id {post.platform_post_id} · {post.impressions} impressions · {post.likes} likes ·
          {' '}{post.reposts} reposts · {post.link_clicks} clicks
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {!editing && <button style={S.btn()} onClick={() => setEditing(true)} disabled={busy}>Edit</button>}
          <button style={S.btn(CYAN)} onClick={() => act('approve')} disabled={busy}>Approve</button>
          <button style={S.btn(RED)} onClick={() => act('reject', { reason: 'rejected in review' })} disabled={busy}>Reject</button>
          <button style={S.btn('#4ef88a')} onClick={() => act('publish')} disabled={busy}>Publish now</button>
          <input
            style={{ ...S.input, width: 190 }} type="datetime-local"
            value={schedule} onChange={(e) => setSchedule(e.target.value)}
          />
          <button
            style={S.btn()} disabled={busy || !schedule}
            onClick={() => act('schedule', { scheduled_at: new Date(schedule).toISOString() })}
          >Schedule</button>
          {violations.length > 0 && (
            <button style={S.btn(AMBER)} onClick={() => act('publish', { force: true })} disabled={busy}>
              Override &amp; publish
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ page */

export default function SIE() {
  const [tab, setTab]         = useState('queue');
  const [posts, setPosts]     = useState([]);
  const [targets, setTargets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [status, setStatus]   = useState(null);
  const [signals, setSignals] = useState([]);
  const [sess, setSess]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState(null);

  const [genKind, setGenKind] = useState('signal_alert');
  const [genTopic, setGenTopic] = useState('');
  const [genPeriod, setGenPeriod] = useState('daily');
  const [genPlatforms, setGenPlatforms] = useState(['x']);

  const say = useCallback((msg, isErr = false) => {
    setToast({ msg, isErr });
    setTimeout(() => setToast(null), 6000);
  }, []);

  const loadQueue = useCallback(async (statusFilter) => {
    setLoading(true);
    try {
      const { data } = await api().get('/posts', { params: statusFilter ? { status: statusFilter } : {} });
      setPosts(data.posts);
    } catch (e) { say(`Load failed: ${e.message}`, true); }
    finally { setLoading(false); }
  }, [say]);

  useEffect(() => {
    api().get('/status').then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'queue')     loadQueue();
    if (tab === 'published') loadQueue('published');
    if (tab === 'engage')    api().get('/targets').then(({ data }) => setTargets(data.targets)).catch(() => {});
    if (tab === 'analytics') api().get('/analytics').then(({ data }) => setAnalytics(data)).catch((e) => say(e.message, true));
    if (tab === 'signals')   api().get('/signals').then(({ data }) => setSignals(data.signals)).catch(() => {});
    if (tab === 'session')   api().get('/session/current')
      .then(({ data }) => setSess(data))
      .catch((e) => say((e.response && e.response.data && e.response.data.error) || e.message, true));
  }, [tab, loadQueue, say]);

  const upsert = (p) => setPosts((cur) => cur.map((x2) => (x2.id === p.id ? { ...x2, ...p } : x2)));

  const generate = async () => {
    setLoading(true);
    try {
      const body = { kind: genKind, platforms: genPlatforms };
      if (genKind === 'educational' || genKind === 'research_thread') body.topic = genTopic;
      if (genKind === 'market_intel') body.period = genPeriod;
      const { data } = await api().post('/generate', body);
      setPosts((cur) => [...data.posts, ...cur]);
      setTab('queue');
      say(`Generated ${data.posts.length} draft${data.posts.length > 1 ? 's' : ''} with ${data.model}`);
    } catch (e) {
      const d = e.response && e.response.data;
      say(`Generation failed: ${(d && d.error) || e.message}`, true);
    } finally { setLoading(false); }
  };

  const runJob = async (path, label) => {
    setLoading(true);
    try {
      const { data } = await api().post(path);
      say(`${label}: ${JSON.stringify(data).slice(0, 220)}`);
    } catch (e) { say(`${label} failed: ${e.message}`, true); }
    finally { setLoading(false); }
  };

  const targetAct = async (id, action, body) => {
    try {
      await api().post(`/targets/${id}/${action}`, body || {});
      const { data } = await api().get('/targets');
      setTargets(data.targets);
      say(`Target ${id}: ${action} ok`);
    } catch (e) { say(`Target ${action} failed: ${e.message}`, true); }
  };

  const queueCounts = posts.reduce((a, p) => ({ ...a, [p.status]: (a[p.status] || 0) + 1 }), {});

  return (
    <div style={S.page}>
      <div style={S.head}>
        <div>
          <h1 style={S.h1}>XYR Social Intelligence Engine</h1>
          <div style={S.sub}>
            {status
              ? `${status.model} · X ${status.x_mode.replace('_', ' ')} · ingest ${status.ingest_token_configured ? 'secured' : 'localhost only'}`
              : 'connecting…'}
          </div>
        </div>
        {loading && <span style={S.pill(CYAN)}>working…</span>}
      </div>

      {toast && <div style={S.banner(toast.isErr ? RED : CYAN)}>{toast.msg}</div>}

      {status && !status.anthropic_configured && (
        <div style={S.banner(RED)}>
          ANTHROPIC_API_KEY is not set in model_data_in_db/.env — content generation will fail.
        </div>
      )}
      {status && !status.x_configured && (
        <div style={S.banner(AMBER)}>
          DRY RUN — no X credentials. Publishing logs the copy and records a placeholder id instead of
          posting. Engagement discovery and impression/profile-visit metrics need a paid X API tier.
        </div>
      )}

      <div style={S.tabs}>
        {[['queue', `queue${queueCounts.pending_review ? ` (${queueCounts.pending_review})` : ''}`],
          ['session', 'session card'], ['generate', 'generate'], ['engage', 'engage'],
          ['published', 'published'], ['signals', 'signals'], ['analytics', 'analytics']].map(([k, label]) => (
          <div key={k} style={S.tab(tab === k)} onClick={() => setTab(k)}>{label}</div>
        ))}
      </div>

      {tab === 'queue' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button style={S.btn()} onClick={() => loadQueue()}>Refresh</button>
            <button style={S.btn(CYAN)} onClick={() => runJob('/scheduler/run', 'Scheduler')}>Run scheduler</button>
          </div>
          {posts.filter((p) => p.status !== 'published' && p.status !== 'archived').length === 0
            ? <div style={S.empty}>queue is empty — generate content or ingest a signal</div>
            : posts.filter((p) => p.status !== 'published' && p.status !== 'archived')
                   .map((p) => <PostCard key={p.id} post={p} onChange={upsert} onToast={say} />)}
        </>
      )}

      {tab === 'session' && (
        sess ? (
          <>
            <div style={{ ...S.meta, marginBottom: 10 }}>
              next gate {new Date(sess.next_boundary).toLocaleTimeString()}
            </div>

            {[['session', '4H SESSION'], ['daily', 'DAILY CLOSE'], ['vpo', 'VOLUME PROFILE 30D'], ['vpo2y', 'VOLUME PROFILE 2Y']].map(([kind, label]) => {
              const rd = sess.readings[kind];
              if (!rd) {
                return (
                  <div key={kind} style={S.card}>
                    <div style={S.row}>
                      <span style={S.pill('#333')}>{label}</span>
                      <span style={S.meta}>not readable yet</span>
                    </div>
                    <div style={{ ...S.note, color: '#333' }}>
                      {kind === 'daily'
                        ? 'Daily R becomes readable after the 4:00 PM ET close.'
                        : kind === 'vpo2y'
                          ? 'Two-year profile unavailable — the daily kline fetch failed.'
                          : 'No data for this reading yet.'}
                    </div>
                  </div>
                );
              }
              return (
                <div key={kind} style={S.card}>
                  <div style={S.row}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={S.pill(rd.card.color || CYAN)}>{label}</span>
                      <span style={S.pill('#444')}>{rd.card.gate}</span>
                      <span style={S.pill(rd.already_carded ? '#4ef88a' : AMBER)}>
                        {rd.already_carded ? 'carded' : 'not yet carded'}
                      </span>
                    </div>
                    <span style={S.meta}>
                      {kind === 'vpo' || kind === 'vpo2y'
                        ? `poc ${Number(rd.card.poc).toLocaleString()} · ${rd.card.rows.length ? `${rd.card.rows.length} rows` : 'levels only'}`
                        : `R ${rd.card.r >= 0 ? '+' : ''}${rd.card.r}`}
                    </span>
                  </div>

                  <SessionCard card={rd.card} />

                  <div style={{ ...S.meta, margin: '10px 0 4px' }}>caption — post with the image</div>
                  <div style={S.part}>{rd.preview_text}</div>

                  <div style={{ ...S.meta, margin: '10px 0 4px' }}>reply — post as a reply, keeps the link out of the body</div>
                  <div style={{ ...S.part, color: '#5a5a5a' }}>{rd.link_reply}</div>

                  <div style={{ marginTop: 10 }}>
                    <button style={S.btn(CYAN)} onClick={async () => {
                      setLoading(true);
                      try {
                        const { data } = await api().post('/session/card', { kind, force: rd.already_carded });
                        const r0 = data.results[0] || {};
                        say(r0.status === 'created'
                          ? `${label} queued as post #${r0.post.id}`
                          : `Skipped: ${r0.reason || r0.error}`);
                        const r = await api().get('/session/current');
                        setSess(r.data);
                      } catch (e) { say(`Card build failed: ${e.message}`, true); }
                      finally { setLoading(false); }
                    }}>{rd.already_carded ? 'Re-card' : 'Queue this card'}</button>
                    <button style={S.btn('#4a6660')} onClick={() => {
                      navigator.clipboard.writeText(rd.preview_text)
                        .then(() => say('Caption copied'))
                        .catch(() => say('Clipboard blocked', true));
                    }}>Copy caption</button>
                    <button style={S.btn('#4a6660')} onClick={() => {
                      navigator.clipboard.writeText(rd.link_reply)
                        .then(() => say('Reply copied'))
                        .catch(() => say('Clipboard blocked', true));
                    }}>Copy reply</button>
                  </div>
                </div>
              );
            })}

            <div style={{ ...S.note, color: '#333' }}>
              Session cards are queued at 3:00 AM, 9:30 AM and 4:00 PM ET — the SQNCR's own gates.
              Daily close and the 30-day profile are queued at the 4:00 PM gate; the two-year profile
              once a week, at Friday's 4:00 PM close. R is the value the
              floating window shows: {'(rsi / 50) − 1'}. The profile is recomputed with the backend's
              own algorithm and only drawn when it reproduces the published POC / VAH / VAL.
            </div>
          </>
        ) : <div style={S.empty}>no readable data yet</div>
      )}

      {tab === 'generate' && (
        <div style={S.card}>
          <div style={{ ...S.meta, marginBottom: 10 }}>content type</div>
          <div style={{ ...S.tabs, marginBottom: 14 }}>
            {['signal_alert', 'market_intel', 'educational', 'research_thread'].map((k) => (
              <div key={k} style={S.tab(genKind === k)} onClick={() => setGenKind(k)}>{k.replace('_', ' ')}</div>
            ))}
          </div>

          {(genKind === 'educational' || genKind === 'research_thread') && (
            <input
              style={{ ...S.input, width: '60%' }} placeholder="topic — e.g. why indicators lag price"
              value={genTopic} onChange={(e) => setGenTopic(e.target.value)}
            />
          )}
          {genKind === 'market_intel' && (
            <select style={S.input} value={genPeriod} onChange={(e) => setGenPeriod(e.target.value)}>
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
              <option value="monthly">monthly</option>
            </select>
          )}

          <div style={{ ...S.meta, margin: '14px 0 8px' }}>platforms</div>
          <div style={S.tabs}>
            {['x', 'linkedin', 'telegram', 'discord'].map((p) => (
              <div
                key={p} style={S.tab(genPlatforms.includes(p))}
                onClick={() => setGenPlatforms(
                  genPlatforms.includes(p) ? genPlatforms.filter((q) => q !== p) : [...genPlatforms, p])}
              >{p}</div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <button
              style={S.btn(CYAN)} onClick={generate}
              disabled={loading || !genPlatforms.length ||
                        ((genKind === 'educational' || genKind === 'research_thread') && !genTopic)}
            >{loading ? 'generating…' : 'Generate'}</button>
          </div>
          <div style={{ ...S.note, marginTop: 12, color: '#333' }}>
            Drafts land in the queue as PENDING REVIEW. Anything the compliance validator rejects stays
            as DRAFT with its violations attached — nothing reaches X without an explicit approval.
          </div>
        </div>
      )}

      {tab === 'engage' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button style={S.btn(CYAN)} onClick={() => runJob('/discover', 'Discovery')}>Run discovery</button>
          </div>
          {targets.length === 0
            ? <div style={S.empty}>no conversations pending review</div>
            : targets.map((t) => (
              <div key={t.id} style={S.card}>
                <div style={S.row}>
                  <div><span style={S.pill('#444')}>@{t.author}</span> <span style={S.meta}>{t.keyword}</span></div>
                  {t.url && <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ ...S.meta, color: CYAN }}>view →</a>}
                </div>
                <div style={{ ...S.part, color: '#5a5a5a' }}>{t.text}</div>
                <div style={{ ...S.meta, margin: '10px 0 4px' }}>suggested reply</div>
                <div style={S.part}>{t.suggested_reply}</div>
                <div style={{ marginTop: 8 }}>
                  <button style={S.btn(CYAN)} onClick={() => targetAct(t.id, 'approve')}>Approve → queue</button>
                  <button style={S.btn(RED)} onClick={() => targetAct(t.id, 'reject')}>Reject</button>
                </div>
              </div>
            ))}
        </>
      )}

      {tab === 'published' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button style={S.btn(CYAN)} onClick={() => runJob('/metrics/refresh', 'Metrics refresh')}>Refresh metrics</button>
          </div>
          {posts.length === 0
            ? <div style={S.empty}>nothing published yet</div>
            : posts.map((p) => <PostCard key={p.id} post={p} onChange={upsert} onToast={say} />)}
        </>
      )}

      {tab === 'signals' && (
        <div style={S.card}>
          <table style={S.table}>
            <thead><tr>
              {['id', 'time', 'btc', 'xyr', 'prev', 'type', 'regime', 'vol', 'conf', 'used'].map((h) =>
                <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {signals.map((s) => (
                <tr key={s.id}>
                  <td style={S.td}>{s.id}</td>
                  <td style={S.td}>{new Date(s.timestamp).toLocaleString()}</td>
                  <td style={{ ...S.td, color: '#888' }}>${Number(s.btc_price).toLocaleString()}</td>
                  <td style={{ ...S.td, color: CYAN }}>{s.xyr_value}</td>
                  <td style={S.td}>{s.previous_value ?? '—'}</td>
                  <td style={S.td}>{s.signal_type}</td>
                  <td style={S.td}>{s.market_regime || '—'}</td>
                  <td style={S.td}>{s.volatility_state || '—'}</td>
                  <td style={S.td}>{s.confidence ?? '—'}</td>
                  <td style={S.td}>{s.published ? 'yes' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {signals.length === 0 && <div style={S.empty}>no signals ingested yet</div>}
        </div>
      )}

      {tab === 'analytics' && analytics && (
        <>
          <div style={S.grid}>
            {[['impressions', analytics.funnel.impressions], ['profile visits', analytics.funnel.profile_visits],
              ['link clicks', analytics.funnel.link_clicks], ['registrations', analytics.funnel.registrations],
              ['trials', analytics.funnel.trials], ['premium', analytics.funnel.premium]].map(([l, v]) => (
              <div key={l} style={S.stat}>
                <div style={S.statN}>{Number(v).toLocaleString()}</div>
                <div style={S.statL}>{l}</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ ...S.meta, marginBottom: 10 }}>conversion rates</div>
            {Object.entries(analytics.rates).map(([k, v]) => (
              <div key={k} style={{ ...S.note, display: 'flex', justifyContent: 'space-between' }}>
                <span>{k.replace(/_/g, ' ')}</span>
                <span style={{ color: v === null ? '#2e2e2e' : CYAN }}>{v === null ? '—' : `${v}%`}</span>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ ...S.meta, marginBottom: 10 }}>best performing topics</div>
            <table style={S.table}>
              <thead><tr>{['topic', 'posts', 'impressions', 'engagements', 'clicks'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>{analytics.by_topic.map((t) => (
                <tr key={t.topic}>
                  <td style={S.td}>{t.topic}</td><td style={S.td}>{t.posts}</td>
                  <td style={S.td}>{t.impressions}</td>
                  <td style={{ ...S.td, color: CYAN }}>{t.engagements}</td><td style={S.td}>{t.link_clicks}</td>
                </tr>))}
              </tbody>
            </table>
            {analytics.by_topic.length === 0 && <div style={S.empty}>no published posts yet</div>}
          </div>

          <div style={S.card}>
            <div style={{ ...S.meta, marginBottom: 10 }}>best performing formats</div>
            <table style={S.table}>
              <thead><tr>{['type', 'format', 'posts', 'impressions', 'engagements', 'clicks'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>{analytics.by_format.map((f, i) => (
                <tr key={i}>
                  <td style={S.td}>{f.post_type}</td><td style={S.td}>{f.format}</td><td style={S.td}>{f.posts}</td>
                  <td style={S.td}>{f.impressions}</td>
                  <td style={{ ...S.td, color: CYAN }}>{f.engagements}</td><td style={S.td}>{f.link_clicks}</td>
                </tr>))}
              </tbody>
            </table>
            {analytics.by_format.length === 0 && <div style={S.empty}>no published posts yet</div>}
          </div>
        </>
      )}
    </div>
  );
}
